import React, { useState } from 'react';
import { Mail, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isAlready, setIsAlready] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setIsAlready(false);

    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: email
      });

      if (response.status === 200) {
        setIsSubscribed(true);
        setEmail('');
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setIsAlready(true);
      } else {
        toast({
          title: "Error",
          description: "Failed to subscribe. Please try again later.",
          variant: "destructive"
        });
      }
      console.error('Newsletter subscription error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="newsletter" className="py-24 bg-gradient-to-br from-teal-950 via-black to-gray-900 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-gold-500 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl animate-pulse animation-delay-2000"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="bg-black/60 backdrop-blur-lg border border-teal-900/30 rounded-3xl p-8 md:p-12 lg:p-16 text-center">
            {!isSubscribed ? (
              <>
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gold-500/20 backdrop-blur-sm rounded-full mb-8">
                  <Mail className="text-gold-400" size={40} />
                </div>

                {/* Heading */}
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-teal-50 mb-6">
                  Stay in the Loop
                </h2>
                
                <p className="text-xl text-teal-200 mb-8 max-w-2xl mx-auto">
                  Get exclusive updates, behind-the-scenes content, and be the first to know when 
                  <span className="text-gold-400 font-semibold"> The Gordon Principle </span> 
                  is out now — 21 tracks available.
                </p>

                {/* Benefits */}
                <div className="grid md:grid-cols-3 gap-6 mb-10">
                  <div className="space-y-2">
                    <div className="text-gold-400 font-bold text-lg">Early Access</div>
                    <p className="text-teal-300 text-sm">New releases and singles before anyone else</p>
                  </div>
                  <div className="space-y-2">
                    <div className="text-gold-400 font-bold text-lg">Exclusive Content</div>
                    <p className="text-teal-300 text-sm">Behind-the-scenes and unreleased tracks</p>
                  </div>
                  <div className="space-y-2">
                    <div className="text-gold-400 font-bold text-lg">Special Offers</div>
                    <p className="text-teal-300 text-sm">Merchandise and event announcements</p>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input 
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (isAlready) setIsAlready(false); }}
                      placeholder="Enter your email"
                      className="flex-1 px-6 py-4 bg-black/50 border border-teal-900/50 rounded-full text-teal-50 placeholder-teal-400/50 focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 transition-all duration-200"
                      disabled={isLoading}
                      data-testid="newsletter-page-email"
                    />
                    <button 
                      type="submit"
                      disabled={isLoading}
                      className="bg-gold-500 hover:bg-gold-600 disabled:bg-gray-600 text-black font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-gold-500/50 whitespace-nowrap"
                      data-testid="newsletter-page-submit"
                    >
                      {isLoading ? 'Subscribing...' : 'Subscribe'}
                    </button>
                  </div>
                </form>

                {/* Already subscribed inline message */}
                {isAlready && (
                  <div className="mt-6 max-w-md mx-auto bg-gold-500/10 border border-gold-500/30 rounded-xl px-5 py-4 text-center" data-testid="newsletter-already-msg">
                    <p className="text-gold-400 font-semibold">You're already subscribed!</p>
                    <p className="text-gold-400/70 text-sm mt-1">This email is already on the list — no action needed.</p>
                  </div>
                )}

                <p className="text-teal-400 text-sm mt-6">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </>
            ) : (
              <>
                {/* Success State */}
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 backdrop-blur-sm rounded-full mb-8">
                  <CheckCircle className="text-green-400" size={40} />
                </div>

                <h2 className="text-4xl md:text-5xl font-bold text-teal-50 mb-6">
                  Welcome to the Family!
                </h2>
                
                <p className="text-xl text-teal-200 mb-4 max-w-2xl mx-auto">
                  Thank you for subscribing! A welcome email is on its way to your inbox.
                </p>

                <p className="text-teal-300 text-sm mb-8 max-w-xl mx-auto">
                  Check your inbox (and spam folder) for a welcome email from Brooksgordon with what to expect next — release drops, show announcements, and early access.
                </p>

                <button 
                  onClick={() => {
                    setIsSubscribed(false);
                    setEmail('');
                  }}
                  className="text-gold-400 hover:text-gold-300 underline transition-colors"
                >
                  Subscribe another email
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
